<script lang="ts">
    import * as Card from '@/components/ui/card';
    import { Button } from '@/components/ui/button';
    import { Plus, Calendar } from 'lucide-svelte';
    import SiteVisitCard from './SiteVisitCard.svelte';
    import SiteVisitFormModal from './SiteVisitFormModal.svelte';
    import type Blocks from '@/schemas/public/Blocks';
    import type { Field } from '@/types/types';

    type SiteVisit = {
        id: string;
        title: string;
        purpose: string | null;
        status: string;
        start_date_time: string;
        end_date_time: string;
        notes: string | null;
        findings: string | null;
        employeeAssignments?: any[];
        vehicle?: any;
        visitable_type: string;
        visitable_id: string;
    };

    type Props = {
        siteVisits?: SiteVisit[];
        visitableType: string;
        visitableId: string;
        blocks: (Blocks & { fields: Field[] })[];
        id?: string;
    };

    let { siteVisits = [], visitableType, visitableId, blocks, id }: Props = $props();

    // Local state for site visits - allows immediate updates without prop changes
    let localSiteVisits = $state<SiteVisit[]>([...siteVisits]);

    // Sync local state with prop changes
    $effect(() => {
        localSiteVisits = [...siteVisits];
    });

    let showCreateModal = $state(false);
    let showEditModal = $state(false);
    let selectedSiteVisit = $state<SiteVisit | null>(null);

    function openCreateModal() {
        selectedSiteVisit = null;
        showCreateModal = true;
    }

    function openEditModal(siteVisit: SiteVisit) {
        selectedSiteVisit = siteVisit;
        showEditModal = true;
    }

    function handleSiteVisitCreated(newSiteVisit: SiteVisit) {
        // Only add if not already present (prevents duplicates when Inertia reloads)
        if (!localSiteVisits.some(sv => sv.id === newSiteVisit.id)) {
            localSiteVisits = [...localSiteVisits, newSiteVisit];
        }
        showCreateModal = false;
    }

    function handleSiteVisitUpdated(updatedSiteVisit: SiteVisit) {
        localSiteVisits = localSiteVisits.map(sv =>
            sv.id === updatedSiteVisit.id ? updatedSiteVisit : sv
        );
        showEditModal = false;
        selectedSiteVisit = null;
    }

    function handleSiteVisitDeleted(deletedId: string) {
        localSiteVisits = localSiteVisits.filter(sv => sv.id !== deletedId);
    }

    // Sort site visits by start date (most recent first)
    let sortedSiteVisits = $derived(
        [...localSiteVisits].sort((a, b) =>
            new Date(b.start_date_time).getTime() - new Date(a.start_date_time).getTime()
        )
    );
</script>

<Card.Root class="mb-4" {id}>
    <Card.Header>
        <div class="flex items-center justify-between">
            <div>
                <Card.Title class="flex items-center">
                    Site Visits
                </Card.Title>
                <Card.Description>
                    Schedule and manage site visits
                </Card.Description>
            </div>
            <Button onclick={openCreateModal} size="sm">
                <Plus class="h-4 w-4 mr-2" />
                Schedule Visit
            </Button>
        </div>
    </Card.Header>
    <Card.Content>
        {#if sortedSiteVisits.length === 0}
            <div class="text-center py-8 text-muted-foreground">
                <Calendar class="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p class="mb-2">No site visits scheduled</p>
                <Button variant="outline" size="sm" onclick={openCreateModal}>
                    Schedule your first visit
                </Button>
            </div>
        {:else}
            <div class="space-y-4">
                {#each sortedSiteVisits as siteVisit (siteVisit.id)}
                    <SiteVisitCard
                        {siteVisit}
                        onEdit={() => openEditModal(siteVisit)}
                    />
                {/each}
            </div>
        {/if}
    </Card.Content>
</Card.Root>

<!-- Create Modal -->
<SiteVisitFormModal
    bind:show={showCreateModal}
    {blocks}
    {visitableType}
    {visitableId}
    siteVisit={null}
    onSuccess={handleSiteVisitCreated}
/>

<!-- Edit Modal -->
<SiteVisitFormModal
    bind:show={showEditModal}
    {blocks}
    {visitableType}
    {visitableId}
    siteVisit={selectedSiteVisit}
    onSuccess={handleSiteVisitUpdated}
/>
